import type { ReactNode } from "react";
import { Link } from "react-router";
import { t } from "ttag";

import { DateTime } from "metabase/common/components/DateTime";
import {
  NewTabWarning,
  getNewTabTitle,
} from "metabase/common/components/ExternalLink";
import CS from "metabase/css/core/index.css";
import { Anchor, Box, Card, FixedSizeIcon, Group, Stack } from "metabase/ui";
import * as Urls from "metabase/urls";
import { getUserName } from "metabase/utils/user";
import {
  canNodeHaveOwner,
  getNodeCreatedAt,
  getNodeCreatedBy,
  getNodeDescription,
  getNodeLastEditedAt,
  getNodeLastEditedBy,
  getNodeOwner,
  getNodeTransform,
} from "metabase-enterprise/dependencies/utils";
import type { DependencyNode } from "metabase-types/api";

import S from "./InfoSection.module.css";

type InfoSectionProps = {
  node: DependencyNode;
};

export function InfoSection({ node }: InfoSectionProps) {
  const description = getNodeDescription(node);
  const owner = getNodeOwner(node);
  const canHaveOwner = canNodeHaveOwner(node.type);
  const transform = getNodeTransform(node);
  const createdBy = getNodeCreatedBy(node);
  const createdAt = getNodeCreatedAt(node);
  const editedBy = getNodeLastEditedBy(node);
  const editedAt = getNodeLastEditedAt(node);

  if (
    description == null &&
    !canHaveOwner &&
    transform == null &&
    (createdAt == null || createdBy == null) &&
    (editedAt == null || editedBy == null)
  ) {
    return null;
  }

  return (
    <Card p={0} shadow="none" withBorder role="region" aria-label={t`Info`}>
      {description != null && (
        <InfoSectionItem label={t`Description`}>
          {description.length > 0 ? (
            <Box className={CS.textWrap}>{description}</Box>
          ) : (
            <Box c="text-secondary">{t`No description`}</Box>
          )}
        </InfoSectionItem>
      )}
      {canHaveOwner && (
        <InfoSectionItem label={t`Owner`}>
          {owner != null ? (
            <Box className={CS.textWrap}>{getUserName(owner)}</Box>
          ) : (
            <Box c="text-secondary">{t`No owner`}</Box>
          )}
        </InfoSectionItem>
      )}
      {transform != null && (
        <InfoSectionItem label={t`Generated by`}>
          <Anchor
            component={Link}
            className={CS.textWrap}
            to={Urls.transform(transform.id)}
            target="_blank"
            title={getNewTabTitle()}
          >
            <Group gap="sm" wrap="nowrap">
              <FixedSizeIcon name="transform" />
              {transform.name}
            </Group>
            <NewTabWarning />
          </Anchor>
        </InfoSectionItem>
      )}
      {createdBy != null && createdAt != null && (
        <InfoSectionItem label={t`Created by`} date={createdAt}>
          <Box className={CS.textWrap}>{getUserName(createdBy)}</Box>
        </InfoSectionItem>
      )}
      {editedBy != null && editedAt != null && (
        <InfoSectionItem label={t`Last edited by`} date={editedAt}>
          <Box className={CS.textWrap}>{getUserName(editedBy)}</Box>
        </InfoSectionItem>
      )}
    </Card>
  );
}

type InfoSectionItemProps = {
  label: string;
  date?: string;
  children?: ReactNode;
};

function InfoSectionItem({ label, date, children }: InfoSectionItemProps) {
  return (
    <Stack className={S.section} p="md" gap="xs">
      <Box className={CS.textWrap} c="text-secondary" fz="sm" lh="h5">
        {label}
      </Box>
      <Group lh="h4" justify="space-between" wrap="nowrap">
        {children}
        {date != null && (
          <Box c="text-secondary">
            <DateTime className={CS.textNoWrap} value={date} unit="day" />
          </Box>
        )}
      </Group>
    </Stack>
  );
}
